import { useLang } from '../../i18n/LanguageContext';
import React, { useState } from 'react';
import axios from 'axios';
import { PAYS_LIVRES, NOMS_PAYS } from '../../lib/zonesLivraison';

export default function ProfileInfoForm({ user }) {
    const { t } = useLang();
    const [formData, setFormData] = useState({
        fullName: user.fullName || '',
        address: user.address || '',
        city: user.city || '',
        postalCode: user.postalCode || '',
        country: user.country || '',
        phone: user.phone || ''
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);

        try {
            await axios.patch(`${import.meta.env.VITE_API_URL || 'http://localhost:3333/api'}/auth/profile`, formData, {
                withCredentials: true
            });
            setMessage({ type: 'success', text: t('compte.infos.succes') });

            // Reload user data
            setTimeout(() => {
                window.location.reload();
            }, 1500);
        } catch (error) {
            console.error('Error updating profile:', error);
            setMessage({ type: 'error', text: t('compte.infos.erreur') });
        } finally {
            setLoading(false);
        }
    };

    return (
        <form className="space-y-6" onSubmit={handleSubmit}>
            {message && (
                <div className={`p-4 rounded-lg font-medium ${message.type === 'success' ? 'bg-green-100 text-green-800 border-2 border-green-300' : 'bg-red-100 text-red-800 border-2 border-red-300'}`}>
                    {message.text}
                </div>
            )}

            <div className="mb-6">
                <h3 className="text-lg font-bold text-text mb-4">{t('compte.infos.general')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="fullName" className="block text-sm font-semibold text-text mb-2">{t('compte.infos.nomComplet')}</label>
                        <input
                            type="text"
                            id="fullName"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg border-2 border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-text mb-2">Email</label>
                        <input
                            type="email"
                            value={user.email}
                            disabled
                            className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed"
                        />
                        <small className="text-xs text-text-light mt-1 block">{t('compte.infos.emailFige')}</small>
                    </div>
                </div>
            </div>

            <div>
                <h3 className="text-lg font-bold text-text mb-2">{t('compte.infos.adresseLivraison')}</h3>
                <p className="text-sm text-text-light mb-4">
                    💡 Ces informations seront pré-remplies lors de vos prochaines commandes
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <label htmlFor="address" className="block text-sm font-semibold text-text mb-2">{t('compte.infos.adresse')}</label>
                        <input
                            type="text"
                            id="address"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            placeholder={t('compte.infos.adressePlaceholder')}
                            className="w-full px-4 py-3 rounded-lg border-2 border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                        />
                    </div>

                    <div>
                        <label htmlFor="city" className="block text-sm font-semibold text-text mb-2">{t('compte.infos.ville')}</label>
                        <input
                            type="text"
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            placeholder={t('compte.infos.ville')}
                            className="w-full px-4 py-3 rounded-lg border-2 border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                        />
                    </div>

                    <div>
                        <label htmlFor="postalCode" className="block text-sm font-semibold text-text mb-2">{t('compte.infos.codePostal')}</label>
                        <input
                            type="text"
                            id="postalCode"
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={handleChange}
                            placeholder={t('compte.infos.codePostal')}
                            className="w-full px-4 py-3 rounded-lg border-2 border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                        />
                    </div>

                    <div>
                        <label htmlFor="country" className="block text-sm font-semibold text-text mb-2">{t('compte.infos.pays')}</label>
                        <select
                            id="country"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg border-2 border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white"
                        >
                            <option value="">{t('compte.infos.choisirPays')}</option>
                            {/* Source unique partagée avec le tunnel de commande :
                                les deux listes étaient écrites en dur chacune de
                                son côté et pouvaient diverger en silence. */}
                            {PAYS_LIVRES.map((code) => (
                                <option key={code} value={code}>{NOMS_PAYS[code]}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label htmlFor="phone" className="block text-sm font-semibold text-text mb-2">{t('compte.infos.telephone')}</label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+33 6 12 34 56 78"
                            className="w-full px-4 py-3 rounded-lg border-2 border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                        />
                    </div>
                </div>
            </div>

            <div className="flex justify-end mt-6">
                <button
                    type="submit"
                    className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading}
                >
                    {loading ? t('compte.infos.enregistrement') : t('compte.infos.enregistrer')}
                </button>
            </div>
        </form>
    );
}
